/**
 * CAM (Credit Appraisal Memo) Paper View Component
 *
 * Renders the same CAM data as the Excel export but as an HTML preview
 * inside a PrimeReact Dialog — printable and viewable in-browser.
 */
import React, { useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

// ── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (v) => (v !== null && v !== undefined && v !== '' ? v : '');
const fmtCur = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const num = typeof v === 'string' ? parseFloat(v) : v;
    return isNaN(num) ? 0 : num;
};
const fmtINR = (v) => {
    const num = fmtCur(v);
    if (num === 0) return '₹0';
    return '₹' + Number(num).toLocaleString('en-IN');
};
const fmtPct = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const num = typeof v === 'string' ? parseFloat(v) : v;
    if (isNaN(num)) return '';
    return (num > 1 ? num : num * 100).toFixed(2) + '%';
};

const CAMPaperView = ({
    visible,
    onHide,
    formData = {},
    lead = {},
    selectedProduct = {},
    coApplicants = [],
    ocrPayslipDetails = null,
    individualPayslips = [],
    offers = [],
    selectedOffer = null,
    eligibilityResult = null,
    eligibilityParams = null,
}) => {
    const printRef = useRef(null);

    // ── Field extraction ────────────────────────────────────────────────────
    const gf = (key) => {
        const val = formData[key];
        if (val === null || val === undefined) return '';
        if (typeof val === 'object' && val.name) return val.name;
        if (typeof val === 'object' && val.label) return val.label;
        return val;
    };
    const gcf = (co, key) => {
        if (!co) return '';
        const val = co[key];
        if (val === null || val === undefined) return '';
        if (typeof val === 'object' && val.name) return val.name;
        return val;
    };

    const extractedData = ocrPayslipDetails?.extracted_data || {};
    const finalOffer = selectedOffer || (offers.length > 0 ? offers[0] : null);
    const eb = finalOffer?.eligibility_breakdown || {};

    // ── Derived values ──────────────────────────────────────────────────────
    const netMonthly = parseFloat(gf('net_salary_per_month') || gf('net_salary') || extractedData.net_salary || 0);
    const yearlyBonus = parseFloat(gf('bonus_income') || 0);
    const netAnnual = netMonthly * 12;
    const totalSalaryIncome = netAnnual + yearlyBonus;
    const applicantName = fmt(gf('full_name') || gf('applicant_name') || extractedData.employee_name || lead?.full_name || lead?.name || '');
    const tenure = finalOffer?.tenure || finalOffer?.tenure_months || parseFloat(gf('tenure_in_months') || 0);
    const applicantAge = parseFloat(gf('age') || gf('applicant_age') || 0);
    const ageAtMaturity = applicantAge > 0 && tenure > 0 ? applicantAge + Math.ceil(tenure / 12) : 0;
    const totalObligations = fmtCur(gf('total_existing_loan_emi') || gf('existing_emi'));
    const foirVal = eb.foir ? eb.foir / 100 : 0.7;
    const roiVal = finalOffer?.roi ? finalOffer.roi / 100 : 0;
    const finalLoanAmt = fmtCur(finalOffer?.loan_amount || gf('loan_amount') || eligibilityParams?.loan_amount);
    const maxLoanSalaried = fmtCur(finalOffer?.max_loan_eligibility || eb.eligible_amount || 0);
    const emiPerLac = fmtCur(finalOffer?.emi_per_lac || eb.emi_per_lac || 0);
    const grossOtherIncome = fmtCur(gf('rental_income')) + fmtCur(gf('agricultural_income')) + fmtCur(gf('other_income'));
    const totalBizIncome = fmtCur(gf('business_income') || gf('total_business_income'));
    const totalIncome = totalSalaryIncome + totalBizIncome + grossOtherIncome;
    const abbIncome = fmtCur(gf('abb') || eligibilityParams?.abb || 0) * 12;

    // ── Print handler ───────────────────────────────────────────────────────
    const handlePrint = () => {
        const content = printRef.current;
        if (!content) return;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>CAM Report — ${applicantName || 'Lead'}</title>
                <style>
                    body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #333; }
                    table { border-collapse: collapse; width: 100%; margin-bottom: 12px; }
                    th, td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; vertical-align: top; }
                    th { background: #f0f4ff; font-weight: 600; }
                    .section-header { background: #1e3a5f; color: #fff; font-weight: 700; text-align: center; font-size: 12px; padding: 6px; }
                    .sub-header { background: #e8edf5; font-weight: 600; text-align: center; font-size: 11px; }
                    .label-cell { background: #fafbfc; font-weight: 500; }
                    .value-cell { font-weight: 400; }
                    .total-row td { background: #e8f5e9; font-weight: 700; }
                    .text-right { text-align: right; }
                    .title { text-align: center; font-size: 16px; font-weight: 700; margin-bottom: 4px; color: #1e3a5f; }
                    .subtitle { text-align: center; font-size: 10px; color: #666; margin-bottom: 16px; }
                    @media print { body { margin: 10px; } }
                </style>
            </head>
            <body>${content.innerHTML}</body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
    };

    // ── Styles ──────────────────────────────────────────────────────────────
    const styles = {
        table: { borderCollapse: 'collapse', width: '100%', marginBottom: '12px', fontSize: '0.72rem' },
        th: { border: '1px solid #dee2e6', padding: '4px 8px', textAlign: 'left', background: '#f0f4ff', fontWeight: 600 },
        td: { border: '1px solid #dee2e6', padding: '4px 8px', textAlign: 'left', verticalAlign: 'top' },
        tdLabel: { border: '1px solid #dee2e6', padding: '4px 8px', background: '#fafbfc', fontWeight: 500, width: '30%' },
        tdValue: { border: '1px solid #dee2e6', padding: '4px 8px' },
        sectionHeader: { background: '#1e3a5f', color: '#fff', fontWeight: 700, textAlign: 'center', fontSize: '0.75rem', padding: '6px', border: '1px solid #1e3a5f' },
        subHeader: { background: '#e8edf5', fontWeight: 600, textAlign: 'center', fontSize: '0.72rem', border: '1px solid #dee2e6', padding: '4px 8px' },
        totalRow: { background: '#e8f5e9', fontWeight: 700 },
        textRight: { textAlign: 'right' },
    };

    // helper to render a section header row
    const SectionHeader = ({ children, colSpan = 7 }) => (
        <tr><td colSpan={colSpan} style={styles.sectionHeader}>{children}</td></tr>
    );

    // helper for 2 key-value pairs in a row
    const KVRow2 = ({ l1, v1, l2, v2 }) => (
        <tr>
            <td style={styles.tdLabel}>{l1}</td>
            <td colSpan={2} style={styles.tdValue}>{v1}</td>
            <td style={styles.tdLabel}>{l2}</td>
            <td colSpan={3} style={styles.tdValue}>{v2}</td>
        </tr>
    );

    // helper for applicant row: label + applicant + up to 5 co-applicants
    const AppRow = ({ label, vals, isTotal }) => (
        <tr style={isTotal ? styles.totalRow : {}}>
            <td style={styles.tdLabel}>{label}</td>
            {vals.slice(0, 6).map((v, i) => (
                <td key={i} style={styles.tdValue}>{typeof v === 'number' ? fmtINR(v) : fmt(v)}</td>
            ))}
            {vals.length < 6 && Array(6 - vals.length).fill(null).map((_, i) => (
                <td key={`e${i}`} style={styles.tdValue}>-</td>
            ))}
        </tr>
    );

    // helper for label + value spanning full row
    const LVRow = ({ label, value, isTotal }) => (
        <tr style={isTotal ? styles.totalRow : {}}>
            <td style={styles.tdLabel}>{label}</td>
            <td colSpan={6} style={{ ...styles.tdValue, fontWeight: isTotal ? 700 : 400 }}>{typeof value === 'number' ? fmtINR(value) : fmt(value)}</td>
        </tr>
    );

    const coVals = (key) => coApplicants.slice(0, 5).map(c => gcf(c, key) || 0);
    const pad5 = (arr, def = 0) => { const a = [...arr]; while (a.length < 5) a.push(def); return a; };

    const dialogHeader = (
        <div className="flex align-items-center justify-content-between w-full pr-4">
            <span className="font-semibold">CAM Paper — {applicantName || 'Lead'}</span>
            <div className="flex gap-2">
                <Button icon="pi pi-print" label="Print" className="p-button-outlined p-button-sm" onClick={handlePrint} style={{ fontSize: '0.75rem' }} />
            </div>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={dialogHeader}
            maximizable
            modal
            style={{ width: '90vw', maxWidth: '1100px' }}
            contentStyle={{ padding: '1rem', background: '#fff' }}
            breakpoints={{ '960px': '95vw' }}
        >
            <div ref={printRef}>
                <div style={{ textAlign: 'center', fontSize: '1rem', fontWeight: 700, color: '#1e3a5f', marginBottom: '4px' }}>
                    LOAN ELIGIBILITY WORKING SHEET
                </div>
                <div style={{ textAlign: 'center', fontSize: '0.65rem', color: '#666', marginBottom: '16px' }}>
                    Generated on: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} | Data auto-filled from BureauBox
                </div>

                {/* ═══ PRODUCT DETAILS ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>PRODUCT DETAILS</SectionHeader>
                        <KVRow2 l1="PRODUCT" v1={fmt(selectedProduct?.name || selectedProduct?.product_name)} l2="SCHEME/PURPOSE" v2={fmt(selectedProduct?.loan_type_name || gf('loan_purpose'))} />
                        <KVRow2 l1="SUB PRODUCT" v1={fmt(selectedProduct?.sub_product || selectedProduct?.lender_name)} l2="CMR CATEGORY" v2={fmt(gf('_master_applicant_type') || 'Salaried Indian')} />
                    </tbody>
                </table>

                {/* ═══ CUSTOMER DETAILS ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>CUSTOMER DETAILS</SectionHeader>
                        <tr>
                            <th style={styles.th}></th>
                            <th style={styles.th}>APPLICANT</th>
                            {[1, 2, 3, 4, 5].map(i => <th key={i} style={styles.th}>CO-APP {i}</th>)}
                        </tr>
                        <AppRow label="NAME" vals={[applicantName, ...pad5(coApplicants.slice(0, 5).map(c => fmt(gcf(c, 'name') || gcf(c, 'full_name'))), '-')]} />
                        <AppRow label="INCOME CONSIDERED?" vals={['YES', ...pad5(coApplicants.slice(0, 5).map(c => fmtCur(gcf(c, 'net_salary')) > 0 ? 'YES' : 'NO'), 'NO')]} />
                        <AppRow label="DATE OF BIRTH" vals={[fmt(gf('date_of_birth') || extractedData.date_of_birth), ...pad5(coVals('date_of_birth'), '-')]} />
                        <AppRow label="RELATIONSHIP" vals={['SELF', ...pad5(coApplicants.slice(0, 5).map(c => fmt(gcf(c, 'relationship'))), '-')]} />
                        <AppRow label="AGE (YRS)" vals={[applicantAge || '-', ...pad5(coVals('age'), '-')]} />
                        <AppRow label="AGE AT MATURITY" vals={[ageAtMaturity || '-', ...pad5(coVals('age').map(a => {
                            const ca = parseFloat(a) || 0;
                            return ca > 0 && tenure > 0 ? ca + Math.ceil(tenure / 12) : '-';
                        }), '-')]} />
                        <AppRow label="TYPE (SAL/SENP/SEP)" vals={[fmt(gf('_master_employment_status') || 'SALARIED'), ...pad5(coApplicants.slice(0, 5).map(c => fmt(gcf(c, 'employment_type'))), '-')]} />
                        <AppRow label="CIBIL SCORE" vals={[fmtCur(gf('credit_score') || gf('cibil_score')) || '-', ...pad5(coVals('credit_score'), '-')]} />
                        <AppRow label="CONTACT PHONE" vals={[fmt(gf('phone') || gf('mobile') || lead?.phone), ...pad5(coApplicants.slice(0, 5).map(c => fmt(gcf(c, 'phone') || gcf(c, 'mobile'))), '-')]} />
                    </tbody>
                </table>

                {/* ═══ [A] SALARIED INCOME ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>[A] INCOME CALCULATION (SALARIED)</SectionHeader>
                        <tr>
                            <th style={styles.th}></th>
                            <th style={styles.th}>APPLICANT</th>
                            {[1, 2, 3, 4, 5].map(i => <th key={i} style={styles.th}>CO-APP {i}</th>)}
                        </tr>
                        <AppRow label="NET INCOME (MONTHLY)" vals={[netMonthly, ...pad5(coVals('net_salary'))]} />
                        <AppRow label="YEARLY BONUS + LTA" vals={[yearlyBonus, ...pad5(coVals('bonus_income'))]} />
                        <AppRow label="NET ANNUAL INCOME" vals={[netAnnual, ...pad5(coApplicants.slice(0, 5).map(c => (parseFloat(gcf(c, 'net_salary')) || 0) * 12))]} />
                        <LVRow label="A. TOTAL SALARY INCOME" value={totalSalaryIncome} isTotal />
                    </tbody>
                </table>

                {/* ═══ [B] SELF-EMPLOYED INCOME ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>[B] INCOME CALCULATIONS (SELF-EMPLOYED)</SectionHeader>
                        <tr><td colSpan={7} style={styles.subHeader}>NET-PAT / NET PAT PRIME I / NET PAT PRIME II PROGRAMS</td></tr>
                        <tr>
                            <th style={styles.th}></th>
                            <th style={styles.th}>APPLICANT</th>
                            {[1, 2, 3, 4, 5].map(i => <th key={i} style={styles.th}>CO-APP {i}</th>)}
                        </tr>
                        <AppRow label="Net Profit (Prev Year)" vals={[fmtCur(gf('net_profit_prev_year')), ...pad5(coVals('net_profit_prev_year'))]} />
                        <AppRow label="Net Profit (Current Year)" vals={[fmtCur(gf('net_profit_curr_year') || gf('net_profit')), ...pad5(coVals('net_profit'))]} />
                        <AppRow label="NET PROFIT CONSIDERED" vals={[fmtCur(gf('net_profit')), ...pad5(coVals('net_profit'))]} />
                        <AppRow label="Depreciation (Prev Year)" vals={[fmtCur(gf('depreciation_prev_year')), ...pad5(coVals('depreciation_prev_year'))]} />
                        <AppRow label="Depreciation (Current Year)" vals={[fmtCur(gf('depreciation_curr_year')), ...pad5(coVals('depreciation_curr_year'))]} />
                        <AppRow label="Director/Partner Remuneration" vals={[fmtCur(gf('director_remuneration')), ...pad5(coVals('director_remuneration'))]} />
                        <AppRow label="Interest on Loans" vals={[fmtCur(gf('interest_on_loans')), ...pad5(coVals('interest_on_loans'))]} />
                        <AppRow label="BUSINESS INCOME CONSIDERED" vals={[fmtCur(gf('business_income')), ...pad5(coVals('business_income'))]} />
                        <LVRow label="TOTAL BUSINESS INCOME (NET PAT)" value={fmtCur(gf('total_business_income') || gf('business_income'))} isTotal />
                    </tbody>
                </table>

                {/* ═══ GROSS MARGINS PROGRAM ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>GROSS MARGINS PROGRAM</SectionHeader>
                        <tr>
                            <th style={styles.th}></th>
                            <th style={styles.th}>APPLICANT</th>
                            {[1, 2, 3, 4, 5].map(i => <th key={i} style={styles.th}>CO-APP {i}</th>)}
                        </tr>
                        <AppRow label="Industry Type" vals={[fmt(gf('industry_type') || 'Manufacturing'), ...pad5(coApplicants.slice(0, 5).map(() => 'Service'), 'Service')]} />
                        <AppRow label="Turnover (Prev Year)" vals={[fmtCur(gf('turnover_prev_year')), ...pad5(coVals('turnover_prev_year'))]} />
                        <AppRow label="Turnover (Current Year)" vals={[fmtCur(gf('turnover_curr_year') || gf('gst_turnover')), ...pad5(coVals('gst_turnover'))]} />
                        <AppRow label="Turnover Considered" vals={[fmtCur(gf('turnover_considered') || gf('gst_turnover')), ...pad5(coVals('gst_turnover'))]} />
                        <LVRow label="A. TOTAL BUSINESS INCOME (GM PROGRAM)" value={fmtCur(gf('gm_total_income'))} isTotal />
                    </tbody>
                </table>

                {/* ═══ BANKING PROGRAM ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>BANKING PROGRAM</SectionHeader>
                        <KVRow2 l1="BT/LOAN CLOSURE EMIs" v1={fmtINR(gf('bt_loan_closure_emi'))} l2="12-MONTH ABB" v2={fmtINR(gf('abb') || eligibilityParams?.abb)} />
                        <KVRow2 l1="OBLIGATIONS" v1={fmtINR(totalObligations)} l2="CC/Overdraft Limit" v2={fmtINR(gf('cc_overdraft_limit'))} />
                    </tbody>
                </table>

                {/* ═══ OTHER INCOME ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>OTHER INCOME CALCULATIONS</SectionHeader>
                        <LVRow label="Rental Income" value={fmtCur(gf('rental_income'))} />
                        <LVRow label="A. TOTAL RENTAL INCOME" value={fmtCur(gf('total_rental_income') || gf('rental_income'))} isTotal />
                        <LVRow label="Agricultural Income" value={fmtCur(gf('agricultural_income'))} />
                        <LVRow label="B. TOTAL AGRICULTURAL INCOME" value={fmtCur(gf('agricultural_income'))} isTotal />
                        <LVRow label="Other Income" value={fmtCur(gf('other_income'))} />
                        <LVRow label="C. TOTAL OTHER INCOME" value={fmtCur(gf('other_income'))} isTotal />
                        <LVRow label="GROSS TOTAL OTHER INCOME (A+B+C)" value={grossOtherIncome} isTotal />
                    </tbody>
                </table>

                {/* ═══ INCOME PROGRAM COMPARISON ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>INCOME PROGRAM COMPARISON</SectionHeader>
                        <tr>
                            <th style={styles.th}>INCOME PROGRAM</th>
                            <th style={styles.th}>SALARIED</th>
                            <th style={styles.th}>NET PROFIT</th>
                            <th style={styles.th}>NET PAT PRIME I</th>
                            <th style={styles.th}>NET PAT PRIME II</th>
                            <th style={styles.th}>GROSS MARGINS</th>
                            <th style={styles.th}>BANKING</th>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>ANNUAL SALARY INCOME</td>
                            {[totalSalaryIncome, totalSalaryIncome, totalSalaryIncome, totalSalaryIncome, totalSalaryIncome, '-'].map((v, i) => (
                                <td key={i} style={styles.tdValue}>{typeof v === 'number' ? fmtINR(v) : v}</td>
                            ))}
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>ANNUAL BUSINESS INCOME</td>
                            {[totalBizIncome, totalBizIncome, totalBizIncome, totalBizIncome, totalBizIncome, abbIncome || 0].map((v, i) => (
                                <td key={i} style={styles.tdValue}>{fmtINR(v)}</td>
                            ))}
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>ANNUAL OTHER INCOME</td>
                            {Array(6).fill(grossOtherIncome).map((v, i) => (
                                <td key={i} style={styles.tdValue}>{fmtINR(v)}</td>
                            ))}
                        </tr>
                        <tr style={styles.totalRow}>
                            <td style={styles.tdLabel}>TOTAL INCOME</td>
                            {[totalIncome, totalIncome, totalIncome, totalIncome, totalIncome, abbIncome || 0].map((v, i) => (
                                <td key={i} style={styles.tdValue}>{fmtINR(v)}</td>
                            ))}
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>TOTAL OBLIGATIONS</td>
                            {Array(6).fill(totalObligations).map((v, i) => (
                                <td key={i} style={styles.tdValue}>{fmtINR(v)}</td>
                            ))}
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>FOIR PROPOSED</td>
                            {[foirVal, 0.5, 1, 1.2, 0.6, 0.55].map((v, i) => (
                                <td key={i} style={styles.tdValue}>{fmtPct(v)}</td>
                            ))}
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>TENURE (Months)</td>
                            {Array(6).fill(tenure || 120).map((v, i) => (
                                <td key={i} style={styles.tdValue}>{v}</td>
                            ))}
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>ROI PROPOSED</td>
                            {[roiVal || 0.085, 0.1, 0.1, 0.1, 0.1, 0.1].map((v, i) => (
                                <td key={i} style={styles.tdValue}>{fmtPct(v)}</td>
                            ))}
                        </tr>
                        <tr style={styles.totalRow}>
                            <td style={styles.tdLabel}>MAX LOAN ELIGIBILITY</td>
                            <td style={styles.tdValue}>{fmtINR(maxLoanSalaried)}</td>
                            {Array(5).fill(null).map((_, i) => <td key={i} style={styles.tdValue}>-</td>)}
                        </tr>
                    </tbody>
                </table>

                {/* ═══ FINAL LOAN PARAMETERS ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>FINAL LOAN PARAMETERS</SectionHeader>
                        <KVRow2 l1="FINAL PROGRAM" v1={fmt(gf('_master_employment_status') || 'SALARIED') + ' PROGRAM'} l2="FINAL LOAN AMOUNT" v2={fmtINR(finalLoanAmt)} />
                        <KVRow2 l1="LOAN AMOUNT" v1={fmtINR(finalLoanAmt)} l2="FOIR (%)" v2={eb.foir ? `${eb.foir}%` : fmtPct(foirVal)} />
                        <KVRow2 l1="TENURE (Months)" v1={tenure || '-'} l2="EMI PER LAC" v2={fmtINR(emiPerLac)} />
                        <KVRow2 l1="ROI" v1={roiVal ? fmtPct(roiVal) : '-'} l2="EMI OF THE LOAN" v2={fmtINR(finalOffer?.emi)} />
                        <KVRow2 l1="TOTAL EMI" v1={fmtINR(finalOffer?.emi)} l2="PROCESSING FEES" v2={finalOffer?.processing_fee ? `${finalOffer.processing_fee}` : '-'} />
                    </tbody>
                </table>

                {/* ═══ PROPERTY DETAILS ═══ */}
                <table style={styles.table}>
                    <tbody>
                        <SectionHeader>PROPERTY DETAILS</SectionHeader>
                        <tr><td colSpan={7} style={{ ...styles.td, fontSize: '0.65rem', color: '#666', fontStyle: 'italic' }}>2 Valuations mandatory for: Loan&gt;50 Lacs, LAP (Land/Industrial), Net PAT Prime I &amp; II</td></tr>
                        <KVRow2 l1="PROPERTY ADDRESS" v1={fmt(gf('property_address')) || '-'} l2="PROPERTY TYPE" v2={fmt(gf('property_type')) || '-'} />
                        <KVRow2 l1="PROPOSED OWNER" v1={fmt(gf('property_owner')) || '-'} l2="CURRENT OWNER" v2={fmt(gf('current_owner')) || '-'} />
                        <KVRow2 l1="USAGE" v1={fmt(gf('property_usage')) || '-'} l2="STAGE OF CONSTRUCTION" v2={fmt(gf('construction_stage')) || '-'} />
                    </tbody>
                </table>

                {/* ═══ PAYSLIP OCR DETAILS ═══ */}
                {(ocrPayslipDetails || individualPayslips.length > 0) && (
                    <table style={styles.table}>
                        <tbody>
                            <SectionHeader>PAYSLIP DETAILS (OCR EXTRACTED)</SectionHeader>
                            {individualPayslips.length > 1 ? (
                                <>
                                    <tr>
                                        <th style={styles.th}>FIELD</th>
                                        {individualPayslips.map((p, i) => (
                                            <th key={i} style={styles.th}>{fmt(p.extracted_data?.pay_period || `Payslip ${i + 1}`)}</th>
                                        ))}
                                        <th style={styles.th}>AVERAGE</th>
                                        {Array(Math.max(0, 5 - individualPayslips.length)).fill(null).map((_, i) => <th key={`e${i}`} style={styles.th}></th>)}
                                    </tr>
                                    {[
                                        { key: 'basic_salary', label: 'Basic Salary' },
                                        { key: 'gross_salary', label: 'Gross Salary' },
                                        { key: 'total_deductions', label: 'Total Deductions' },
                                        { key: 'net_salary', label: 'Net Salary' },
                                    ].map(({ key, label }) => {
                                        const vals = individualPayslips.map(p => fmtCur(p.extracted_data?.[key]));
                                        if (!vals.some(v => v > 0)) return null;
                                        return (
                                            <tr key={key}>
                                                <td style={styles.tdLabel}>{label}</td>
                                                {vals.map((v, i) => <td key={i} style={styles.tdValue}>{fmtINR(v)}</td>)}
                                                <td style={{ ...styles.tdValue, fontWeight: 600 }}>{fmtINR(extractedData[key])}</td>
                                                {Array(Math.max(0, 5 - individualPayslips.length)).fill(null).map((_, i) => <td key={`e${i}`} style={styles.tdValue}></td>)}
                                            </tr>
                                        );
                                    })}
                                </>
                            ) : (
                                <>
                                    <LVRow label="Pay Period" value={fmt(extractedData.pay_period)} />
                                    <LVRow label="Employee Name" value={fmt(extractedData.employee_name)} />
                                    <LVRow label="Company Name" value={fmt(extractedData.company_name)} />
                                    <LVRow label="Basic Salary" value={fmtCur(extractedData.basic_salary)} />
                                    <LVRow label="Gross Salary" value={fmtCur(extractedData.gross_salary)} />
                                    <LVRow label="Total Deductions" value={fmtCur(extractedData.total_deductions)} />
                                    <LVRow label="Net Salary" value={fmtCur(extractedData.net_salary)} />
                                </>
                            )}
                        </tbody>
                    </table>
                )}

                {/* ═══ GENERATED OFFERS ═══ */}
                {offers.length > 0 && (
                    <table style={styles.table}>
                        <tbody>
                            <SectionHeader>GENERATED OFFERS</SectionHeader>
                            <tr>
                                <th style={styles.th}>S.No</th>
                                <th style={styles.th}>Bank / Lender</th>
                                <th style={styles.th}>ROI (%)</th>
                                <th style={styles.th}>EMI</th>
                                <th style={styles.th}>Loan Amount</th>
                                <th style={styles.th}>Tenure</th>
                                <th style={styles.th}>Processing Fee</th>
                            </tr>
                            {offers.map((offer, idx) => (
                                <tr key={idx} style={selectedOffer && selectedOffer === offer ? { background: '#e8f5e9' } : {}}>
                                    <td style={styles.tdValue}>{idx + 1}</td>
                                    <td style={styles.tdValue}>{fmt(offer.bank_name || offer.lender_name)}</td>
                                    <td style={styles.tdValue}>{offer.roi ? `${offer.roi}%` : '-'}</td>
                                    <td style={styles.tdValue}>{offer.emi ? fmtINR(offer.emi) : '-'}</td>
                                    <td style={styles.tdValue}>{offer.loan_amount ? fmtINR(offer.loan_amount) : '-'}</td>
                                    <td style={styles.tdValue}>{offer.tenure || offer.tenure_months || '-'}</td>
                                    <td style={styles.tdValue}>{offer.processing_fee || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* ═══ ELIGIBILITY BREAKDOWN ═══ */}
                {eb.applicants_breakdown && eb.applicants_breakdown.length > 0 && (
                    <table style={styles.table}>
                        <tbody>
                            <SectionHeader>ELIGIBILITY BREAKDOWN</SectionHeader>
                            <KVRow2 l1="BANK" v1={fmt(eb.bank_name || finalOffer?.bank_name)} l2="ROI" v2={eb.roi ? `${eb.roi}%` : '-'} />
                            <KVRow2 l1="FOIR" v1={eb.foir ? `${eb.foir}%` : '-'} l2="EMI PER LAC" v2={fmtINR(eb.emi_per_lac)} />
                            <LVRow label="TOTAL INCOME" value={fmtCur(eb.total_income)} />
                            <LVRow label="INCOME AFTER FOIR" value={fmtCur(eb.income_after_foir)} />
                            <LVRow label="TOTAL OBLIGATIONS" value={fmtCur(eb.total_obligations)} />
                            <LVRow label="AVAILABLE FOR EMI" value={fmtCur(eb.available_for_emi)} />
                            <LVRow label="ELIGIBLE AMOUNT" value={fmtCur(eb.eligible_amount)} isTotal />
                            <LVRow label="STATUS" value={eb.is_eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'} isTotal />
                        </tbody>
                    </table>
                )}

                {/* ═══ ELIGIBILITY RESULT ═══ */}
                {eligibilityResult && (
                    <table style={styles.table}>
                        <tbody>
                            <SectionHeader>ELIGIBILITY RESULT</SectionHeader>
                            <LVRow label="STATUS" value={eligibilityResult.passed ? 'ELIGIBLE' : 'NOT ELIGIBLE'} isTotal />
                            <LVRow label="ELIGIBLE AMOUNT" value={fmtCur(eligibilityResult.loan_eligibility?.eligible_amount)} isTotal />
                        </tbody>
                    </table>
                )}
            </div>
        </Dialog>
    );
};

export default CAMPaperView;
